import { useEffect, useState, useCallback, useRef, useSyncExternalStore } from 'react';
import { getPendingDetails, purgeOldPhotoDrafts } from '../services/db';
import type { PendingDetail } from '../services/db';
import { flushQueueFromClient, resetAllRetriesClient, setSyncProgressCallback, pushQueueStatus } from '../services/offlineSyncClient';
import type { FlushStats } from '../services/offlineSyncClient';
import {
  setUserOfflineMode,
  isUserOfflineMode,
  isManualOfflineExpired,
  touchManualOfflineClock,
  getLastServerResponseAt,
} from '../services/networkStatus';
import { addDiscardNotice } from '../services/discardNotices';
import { telemetryLog } from '../services/telemetry';
import { isTokenValid } from '../services/storage';

// ── Constantes de coordinación ─────────────────────────────────────────────
// Throttle de flushes NO forzados. El mutex inFlight ya impide ejecuciones
// concurrentes, así que esto solo limita el ritmo al que el poll dispara
// tryFlush() en vacío. Eventos significativos (online, connection.change,
// vuelta a foreground) usan force=true y no pasan por este throttle.
const MIN_FLUSH_INTERVAL_MS = 2000;

// Cadencia de polling adaptativa. POLL_BUSY corto cubre el caso iOS Safari
// donde el evento `online` no se dispara al recuperar cobertura — el
// polling detecta la transición y arranca el flush.
const POLL_IDLE_MS = 60_000;
const POLL_BUSY_MS = 1_500;

// Tras detectar que la red volvió, reintentamos varias veces escalonado
// para cubrir la ventana de estabilización TCP/DNS móvil. El primer retry
// a 150ms cubre el caso de que el evento `online` llegue antes de que la
// pila de red esté realmente lista.
const ONLINE_RETRY_DELAYS_MS = [150, 500, 1500, 4000, 9000];

// Caducidad del modo offline manual. "Trabajar offline" resuelve el momento de
// red lenta, pero mientras está activo la cola NO tiene NINGUNA vía de salida:
// ni flush de cliente, ni FLUSH_PENDING al SW, ni registro de Background Sync
// (todos guardados por isUserOfflineMode). Si al controlador se le olvida
// volver, sus cargas se quedan en el móvil indefinidamente — pasó en campo:
// dos cargas atrapadas una tarde entera.
const MANUAL_OFFLINE_TTL_MS = 20 * 60 * 1000;

let lastFlushAt = 0;
let persistStorageRequested = false;

// Pide al navegador que marque el storage como persistente. En Chromium
// (Chrome/Edge Android) suele aceptarse silenciosamente cuando la engagement
// es alta — tener push subscrito ayuda. Garantiza que la cola pendiente no
// se evicta por presión de almacenamiento aunque la PWA no esté instalada.
// Una sola petición por sesión es suficiente.
async function ensurePersistentStorage(): Promise<void> {
  if (persistStorageRequested) return;
  persistStorageRequested = true;
  try {
    const sm = navigator.storage;
    if (!sm?.persist || !sm?.persisted) return;
    if (await sm.persisted()) return;
    await sm.persist();
  } catch { /* sin soporte / silencio */ }
}

// Registra el Background Sync del SW. Lo llamamos al encolar y en cada
// tryFlush. NO requiere estar online — el navegador acepta el registro y
// dispara el evento cuando detecta conectividad. Esto es lo que permite
// que, si el usuario cierra la pestaña justo tras encolar, la subida
// ocurra cuando vuelva la red (sin necesidad de reabrir la app).
async function ensureBackgroundSyncRegistered(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.ready;
    if (!reg) return;
    const swReg = reg as ServiceWorkerRegistration & {
      sync?: { register(tag: string): Promise<void> };
    };
    if (swReg.sync) await swReg.sync.register('sync-pending');
  } catch { /* sin soporte / silencio */ }
}

// ── Disparador del flush ────────────────────────────────────────────────────
// Estrategia HÍBRIDA:
// 1. Si la pestaña está visible Y online, ejecutamos el flush en el cliente
//    (axios) — código demostradamente funcional, mismo flujo que el online.
// 2. SIEMPRE notificamos al SW (postMessage) para que también lo intente; útil
//    cuando la app pasa a background a mitad del sync, o cuando el cliente
//    no consigue terminar.
// 3. Registramos Background Sync (Chromium) como respaldo para app cerrada,
//    independientemente de si hay red ahora — el navegador lo disparará
//    cuando la haya.
function tryFlush(force = false, resetRetries = false): void {
  // Sin sesión (pantalla de login, token caducado) no hay nada que drenar:
  // cada petición acabaría en 401. El coordinador vive en App y arranca
  // también en /login, y ahí el flush de arranque leía la cola en IndexedDB
  // sin ningún motivo; en un iPhone con el almacenamiento roto eso era un
  // evento en Sentry por cada arranque (PWA-15/16, 21 sep 2026). El sondeo,
  // el cambio de red y la vuelta a primer plano vuelven a pasar por aquí en
  // cuanto haya sesión, así que tras el login la cola se drena igual.
  if (!isTokenValid()) return;
  // Si el usuario eligió "Trabajar offline" no tocamos nada — ni cliente
  // (que ya tiene su guarda) ni SW (que NO conoce el modo manual). Sin
  // este corte, una notificación FLUSH_PENDING al SW provocaría que éste
  // subiera la cola al servidor mientras el cliente aún piensa que está
  // offline → al volver atrás, la lista aparece vacía (fantasma).
  if (isUserOfflineMode()) return;

  const now = Date.now();
  if (!force && now - lastFlushAt < MIN_FLUSH_INTERVAL_MS) return;
  lastFlushAt = now;

  // 1. Flush en cliente (principal). Si pidieron reset de retries lo hacemos
  //    antes de procesar. El flag isFlushing lo expone el hook useIsFlushing
  //    para que el banner pueda quedarse visible toda la sesión.
  const clientFlush = async () => {
    setFlushing(true);
    try {
      if (resetRetries) {
        await resetAllRetriesClient().catch(() => {});
      }
      await flushQueueFromClient().catch(() => {});
    } finally {
      await refreshPending();
      setFlushing(false);
    }
  };
  void clientFlush();

  // 2. Registramos el sync sin esperar a estar online, y notificamos al SW
  //    como respaldo (no esperamos respuesta).
  void ensureBackgroundSyncRegistered();
  navigator.serviceWorker?.ready
    .then((reg) => {
      reg.active?.postMessage({ type: 'FLUSH_PENDING', resetRetries });
    })
    .catch(() => {});
}

// ── Caducidad del modo offline manual ──────────────────────────────────────
// Al vencer el plazo la prueba la paga la COLA, no el usuario: salimos del modo,
// intentamos drenar en segundo plano y, si el backend no da señales de vida,
// volvemos al modo manual con el reloj a cero. Así el controlador no recupera las esperas de
// 10-25s en el formulario por una comprobación nuestra.
//
// Condiciones para intentarlo siquiera:
//   - Hay trabajo atrapado. Sin cola no hay nada que salvar y su elección se
//     respeta sin plazo (= comportamiento actual, cero riesgo de empeorar).
//   - Hay red real. Sin ella la prueba no diría nada y gastaría el turno.
//
// Mientras dura, `expiringManualOffline` corta los demás disparos de tryFlush:
// esos SÍ avisan al SW, y un SW subiendo por su cuenta después de que la prueba
// nos devuelva a offline es exactamente el "fantasma" que documenta tryFlush
// (el cliente se cree offline y la carga desaparece de su lista).
let expiringManualOffline = false;

// Flush SOLO de cliente: ni FLUSH_PENDING al SW ni registro de Background Sync.
// Es lo que permite que una prueba fallida no deje al SW drenando por detrás.
async function clientOnlyFlush(): Promise<FlushStats | null> {
  setFlushing(true);
  try {
    return (await flushQueueFromClient().catch(() => null)) ?? null;
  } finally {
    await refreshPending();
    setFlushing(false);
  }
}

async function maybeExpireManualOffline(): Promise<void> {
  if (expiringManualOffline) return;
  if (!isUserOfflineMode()) return;
  if (pendingCount === 0) return;
  if (!navigator.onLine) return;
  if (!isManualOfflineExpired(MANUAL_OFFLINE_TTL_MS)) return;
  expiringManualOffline = true;
  try {
    telemetryLog('offline_manual_caducado', { pendientes: pendingCount });
    const probeStartedAt = Date.now();
    setUserOfflineMode(false);
    // SIN reset de retries a propósito: resetAllPendingRetries limpia también
    // targetGoneAt (detalles que ya no existen en el portal) y resucitarlos
    // cada 20 min sería un bucle de 404s y avisos al controlador. Reanimar
    // aparcados sigue siendo cosa exclusiva de "Forzar subida".
    const stats = await clientOnlyFlush();
    // ¿Respondió el backend en algún momento de la prueba? Es la señal honesta
    // de "hay red", y la que faltaba: un item CON FOTOS nunca se completa en la
    // pasada de datos (queda en 'dataSent'), así que exigir `synced > 0`
    // condenaba al fracaso justo el caso más común. Visto en campo el 2 sep: la
    // carga subió con un 200 en 636 ms y aun así devolvíamos al usuario a
    // offline.
    const huboRespuesta = (getLastServerResponseAt() ?? 0) >= probeStartedAt;
    // El propio controlador volvió a "Trabajar offline" mientras probábamos
    // (pulsó el botón ante una petición lenta suya). Su decisión es más reciente
    // que nuestro plazo: no tocamos el estado —ya está donde él lo quiere, y su
    // pulsación reinició el reloj— ni reportamos un fallo de red que no hubo.
    if (isUserOfflineMode()) {
      telemetryLog('offline_manual_reelegido', { subidos: stats?.synced ?? 0 });
      return;
    }
    // Nos quedamos online SOLO con prueba de que la red funciona: el backend
    // respondió durante la prueba, o algún item se completó. Cualquier otro
    // desenlace —silencio total, o el flush no llegó a salir— devuelve al
    // usuario a su modo offline con el reloj a cero, y lo reintentamos dentro de
    // otros 20 min. Es la opción conservadora a propósito: en la duda, mandan él
    // y el estado actual.
    if (huboRespuesta || (stats && stats.synced > 0)) {
      // Red demostrada: nos quedamos online. Lo que quede en cola lo recoge el
      // siguiente tick del poll (1,5s), ya por la vía normal — con SW incluido.
      telemetryLog('offline_manual_reconectado', {
        subidos: stats?.synced ?? 0,
        // Distingue las dos pruebas en el visor: la mayoría de reconexiones
        // buenas serán por respuesta, no por item completado.
        porRespuesta: huboRespuesta,
      });
    } else {
      setUserOfflineMode(true);
      telemetryLog('offline_manual_restaurado', {
        subidos: stats?.synced ?? 0,
        fallidos: stats?.failed ?? 0,
      });
    }
  } finally {
    expiringManualOffline = false;
  }
}

// Cascada de reintentos tras detectar que la red volvió.
function flushAfterOnline(reset = false): void {
  tryFlush(true, reset);
  for (const delay of ONLINE_RETRY_DELAYS_MS) {
    setTimeout(() => {
      if (navigator.onLine) tryFlush(true);
    }, delay);
  }
}

// ── Store singleton del contador de pendientes ─────────────────────────────
// pendingCount = nº de items en cola (1 carga ofline = 1 item).
// pendingWorkUnits = nº TOTAL de operaciones pendientes, contando cada
//   foto adjunta como una unidad extra. Una carga con 6 fotos = 7 units.
//   Esto es lo que el banner usa para mostrar un porcentaje real de
//   progreso conforme las fotos van subiendo.
// isFlushing = true mientras flushQueueFromClient está corriendo, para
//   que el banner se mantenga visible durante toda la sesión y no
//   parpadee entre petición y petición.
let pendingCount = 0;
let pendingWorkUnits = 0;
// Solo FOTOS pendientes (sin contar los items). Baja de una en una conforme la
// cola CONFIRMA cada foto (notifySyncProgress tras el persist por foto).
let pendingPhotoUnits = 0;
let isFlushing = false;
const subscribers = new Set<() => void>();

function notifySubscribers() {
  subscribers.forEach((cb) => cb());
}

function setFlushing(value: boolean) {
  if (isFlushing === value) return;
  isFlushing = value;
  notifySubscribers();
}

// Recordatorio de pendientes envejecidos. Si algún item lleva >24h en cola es
// señal de que algo impide drenarla (sin cobertura estable, item atascado) y el
// controller debe saberlo — como máximo una vez al día (espejo del recordatorio
// de Android), por el canal de avisos NO bloqueantes.
const OLD_PENDING_AGE_MS = 24 * 60 * 60 * 1000;
const REMINDER_THROTTLE_MS = 24 * 60 * 60 * 1000;
const REMINDER_KEY = 'PENDING_REMINDER_LAST';

// Aviso de detalle destino borrado en el portal. La capa de sync marca el item
// (targetGoneAt) y deja de reintentarlo; aquí — la capa de UI — avisamos al
// usuario UNA vez por sesión. Se hace aquí (y no en offlineSyncClient) para
// cubrir también los items que marque el SW en background: refreshPending corre
// al abrir la app y los descubre. El Set en memoria garantiza "una vez"; tras
// un reload puede re-avisar una vez (recordatorio útil de trabajo pendiente).
const targetGoneNotified = new Set<number>();
function maybeNotifyTargetGone(items: PendingDetail[]): void {
  for (const it of items) {
    if (it.targetGoneAt == null || targetGoneNotified.has(it.id)) continue;
    targetGoneNotified.add(it.id);
    const editId = it.idCargoDetail || it.detail.idCargoDetail || 0;
    const n = Array.isArray(it.detail.listNewImages) ? it.detail.listNewImages.length : 0;
    telemetryLog('detalle_no_existe', { idCargoDetail: editId, fotos: n });
    addDiscardNotice('offline.notice.detailGone', { count: n });
  }
}

// Aviso de item RECHAZADO por el servidor: el POST de datos recibe 4xx/5xx
// una y otra vez (la capa de sync lleva la cuenta en serverRejects). Sin este
// aviso el controlador solo ve "1 pendiente" durante horas y nadie — ni él ni
// nosotros — sabe qué contenedor es ni qué hacer. Se avisa una vez por item y
// sesión a partir del tercer rechazo consecutivo; el item se sigue
// reintentando con su backoff por si el servidor se recupera.
const SERVER_REJECT_NOTIFY_AT = 3;
const serverRejectNotified = new Set<number>();
function maybeNotifyServerRejected(items: PendingDetail[]): void {
  for (const it of items) {
    const n = it.serverRejects ?? 0;
    if (n < SERVER_REJECT_NOTIFY_AT || serverRejectNotified.has(it.id)) continue;
    serverRejectNotified.add(it.id);
    const container = (it.detail.containerNumber ?? '').trim() || '?';
    const status = (it.serverRejectMsg ?? '').slice(0, 3) || '5xx';
    telemetryLog('rechazado_por_servidor', {
      item: it.id,
      idCargo: it.idCargo,
      contenedor: container,
      rechazos: n,
      respuesta: it.serverRejectMsg ?? '',
    });
    addDiscardNotice('offline.notice.serverRejected', { container, status, count: n });
  }
}

function maybeRemindOldPending(items: PendingDetail[]): void {
  try {
    const now = Date.now();
    const old = items.filter((i) => i.createdAt && now - i.createdAt > OLD_PENDING_AGE_MS);
    if (old.length === 0) return;
    const last = Number(localStorage.getItem(REMINDER_KEY) || 0);
    if (now - last < REMINDER_THROTTLE_MS) return;
    localStorage.setItem(REMINDER_KEY, String(now));
    const oldestHours = Math.round((now - Math.min(...old.map((i) => i.createdAt))) / 3_600_000);
    addDiscardNotice('offline.notice.oldPending', { count: old.length, hours: oldestHours });
    telemetryLog('recordatorio_pendientes', {
      elementos: old.length,
      horasMasAntiguo: oldestHours,
    });
  } catch { /* best-effort: sin localStorage no hay recordatorio */ }
}

async function refreshPending() {
  try {
    const items = await getPendingDetails();
    maybeRemindOldPending(items);
    maybeNotifyTargetGone(items);
    maybeNotifyServerRejected(items);
    const count = items.length;
    let units = 0;
    let photoUnits = 0;
    for (const item of items) {
      units += 1;
      const photos = item.detail.listNewImages;
      if (Array.isArray(photos)) {
        units += photos.length;
        photoUnits += photos.length;
      }
    }
    const changed =
      pendingCount !== count || pendingWorkUnits !== units || pendingPhotoUnits !== photoUnits;
    pendingCount = count;
    pendingWorkUnits = units;
    pendingPhotoUnits = photoUnits;
    if (changed) notifySubscribers();
    // Red de seguridad del status del visor: refreshPending corre en cada poll
    // y tras CUALQUIER acción que toque la cola (encolar, vaciar desde Ajustes,
    // drenar), así que la tarjeta refleja el cambio en segundos. La puerta
    // única dedupea: el coste con el poll activo es casi nulo.
    pushQueueStatus(count, photoUnits, isFlushing);
    if (count > 0) {
      // Cualquier momento en que detectemos pendientes (mount, cambio de
      // estado, tras encolar) es buen momento para asegurar persistencia
      // y, si el usuario no eligió manual offline, registrar el sync.
      void ensurePersistentStorage();
      if (!isUserOfflineMode()) void ensureBackgroundSyncRegistered();
    }
  } catch { /* DB aún no inicializada */ }
}

function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

export function usePendingCount(): number {
  return useSyncExternalStore(subscribe, () => pendingCount, () => 0);
}

// Unidades totales de trabajo pendiente: cada item cuenta 1, cada foto
// adjunta cuenta 1 más. Sirve para mostrar progreso real (incl. fotos)
// en el SlowConnectionBanner.
export function usePendingWorkUnits(): number {
  return useSyncExternalStore(subscribe, () => pendingWorkUnits, () => 0);
}

// Solo fotos pendientes en cola — para un "subiendo fotos… X de Y" fiel,
// actualizado por foto confirmada (no por pasada).
export function usePendingPhotoUnits(): number {
  return useSyncExternalStore(subscribe, () => pendingPhotoUnits, () => 0);
}

// True mientras flushQueueFromClient está activo. Permite al banner
// mantenerse visible durante toda la sesión de sync sin parpadear entre
// petición y petición.
export function useIsFlushing(): boolean {
  return useSyncExternalStore(subscribe, () => isFlushing, () => false);
}

// Acceso síncrono no-React al estado de sync (lo necesita el updateManager
// para decidir si es seguro aplicar una actualización: no queremos reload
// en medio de un flush, ni con cola pendiente).
export function getOfflineSyncSnapshot(): { pendingCount: number; isFlushing: boolean } {
  return { pendingCount, isFlushing };
}
export function subscribeOfflineSync(cb: () => void): () => void {
  return subscribe(cb);
}

// ── Hook coordinador ────────────────────────────────────────────────────────
// Debe montarse UNA SOLA VEZ en el árbol. Replica OfflineQueueCoordinator
// (Android) con tres refuerzos para PWA:
//   1. Detección de transición offline→online por polling — el evento `online`
//      no siempre dispara en iOS Safari y algunos Androids tras quitar modo
//      avión.
//   2. Listeners adicionales (focus, pageshow, visibilitychange) — todos
//      apuntan a "el usuario está mirando la app, conviene reintentar ya".
//   3. Periodic Background Sync API — única forma fiable de sync con la app
//      cerrada en navegadores que la soportan.
export function useSyncCoordinator(): void {
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Registramos un callback en el cliente de sync para que el contador
    // de work units se refresque tras cada foto subida — sin esto, el
    // banner solo se actualizaría al fin del flush completo.
    setSyncProgressCallback(refreshPending);

    refreshPending();
    // Purga de borradores de fotos abandonados (>7 días sin tocar) — una vez
    // por sesión; cualquier mutación reciente renueva su vigencia.
    void purgeOldPhotoDrafts();

    let lastOnline = navigator.onLine;

    const handleBecameOnline = () => {
      // Si el usuario había activado "Trabajar offline" (modo offline manual)
      // y la red real ha vuelto, salimos del modo automáticamente. De lo
      // contrario flushQueueFromClient saldría por la guarda
      // `isOfflineForUser()` y la cola tardaría hasta 2 ciclos de ping
      // (~30s) en procesarse.
      if (isUserOfflineMode()) setUserOfflineMode(false);
      // Reseteamos retries en cada recuperación real de red: items que
      // pudieron acumular 20 fallos durante mala cobertura merecen una
      // oportunidad nueva con la red recién recuperada — sin que el
      // usuario tenga que pulsar "Forzar subida".
      flushAfterOnline(true);
      setTimeout(refreshPending, 1500);
    };

    const onOnline = () => {
      if (!lastOnline) handleBecameOnline();
      lastOnline = true;
    };
    const onOffline = () => {
      lastOnline = false;
      refreshPending();
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    type ConnLike = {
      addEventListener: (e: string, cb: () => void) => void;
      removeEventListener: (e: string, cb: () => void) => void;
      type?: string;
      effectiveType?: string;
    };
    const conn = (navigator as Navigator & { connection?: ConnLike }).connection;
    let lastConnType: string | undefined = conn?.type;
    const onConnChange = () => {
      if (navigator.onLine) {
        if (!lastOnline) {
          handleBecameOnline();
        } else {
          // Transición a WiFi (cell→wifi típicamente) = cambio cualitativo
          // de red: los fallos acumulados por mala cobertura ya no aplican.
          // Reseteamos retries y forzamos flush — el usuario no debería
          // tener que pulsar nada al llegar a casa/oficina.
          const newType = conn?.type;
          const movedToWifi = newType === 'wifi' && lastConnType !== 'wifi';
          if (!expiringManualOffline) tryFlush(true, movedToWifi);
          lastConnType = newType;
        }
        lastOnline = true;
      } else {
        lastOnline = false;
        lastConnType = conn?.type;
      }
      // Cambio de red: refresca el status para que 'cobertura' se actualice al
      // momento (la puerta única dedupea si nada cambió).
      pushQueueStatus(pendingCount, pendingPhotoUnits, isFlushing);
    };
    // En algunos WebView de Android navigator.connection existe pero no es un
    // EventTarget (sin addEventListener): reventaba al montar la app (Sentry
    // PWA-R, /login). Sin listener solo se pierde la reacción inmediata al
    // cambio de red; el poll lo cubre igualmente.
    const connEscucha = typeof conn?.addEventListener === 'function';
    if (connEscucha) conn.addEventListener('change', onConnChange);

    const onSWMessage = (e: MessageEvent) => {
      if (e.data?.type === 'QUEUE_STATE' && typeof e.data.pending === 'number') {
        // El SW solo nos manda el count; refrescamos para recalcular
        // también workUnits leyendo la BD local.
        refreshPending();
      }
    };
    navigator.serviceWorker?.addEventListener('message', onSWMessage);

    // Polling adaptativo + detector de transición offline→online.
    let currentPeriod = pendingCount > 0 ? POLL_BUSY_MS : POLL_IDLE_MS;
    const startPolling = (period: number) => {
      if (pollRef.current) clearInterval(pollRef.current);
      currentPeriod = period;
      pollRef.current = setInterval(() => {
        const nowOnline = navigator.onLine;
        if (nowOnline && !lastOnline) {
          // El evento `online` no se disparó pero el polling sí lo detectó.
          handleBecameOnline();
        } else if (nowOnline && pendingCount > 0 && !isUserOfflineMode() && !expiringManualOffline) {
          // Hay pendientes y red disponible y el usuario NO eligió modo
          // offline manual: dale un empujón aunque ya hayamos disparado
          // hace poco. Si está en modo manual, respetamos su elección y
          // no flusheamos hasta que él vuelva a "online".
          tryFlush();
        }
        lastOnline = nowOnline;
        refreshPending();
        // Con cola pendiente este tick corre cada 1,5s, así que la caducidad se
        // atiende casi al instante (los guardas internos la hacen barata).
        void maybeExpireManualOffline();
        const wantBusy = pendingCount > 0;
        const targetPeriod = wantBusy ? POLL_BUSY_MS : POLL_IDLE_MS;
        if (targetPeriod !== currentPeriod) startPolling(targetPeriod);
      }, period);
    };
    startPolling(currentPeriod);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (navigator.onLine) {
          if (!lastOnline) handleBecameOnline();
          else if (!expiringManualOffline) tryFlush(true);
        }
        lastOnline = navigator.onLine;
        // Encadenado: maybeExpire lee pendingCount, que refreshPending acaba de
        // recalcular. Volver a primer plano es el momento en que el móvil sale
        // del congelado de timers, así que aquí la comprobación es la que salva
        // el caso real.
        void refreshPending().then(() => maybeExpireManualOffline());
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);
    window.addEventListener('pageshow', onVisibility);

    // Periodic Background Sync (Chromium con permiso de PWA instalada).
    navigator.serviceWorker?.ready.then((reg) => {
      const periodicReg = reg as ServiceWorkerRegistration & {
        periodicSync?: { register(tag: string, options: { minInterval: number }): Promise<void> };
      };
      periodicReg.periodicSync?.register('sync-pending-periodic', {
        minInterval: 15 * 60 * 1000,
      }).catch(() => {});
    }).catch(() => {});

    // Arranque con pendientes (incluye F5 a mitad de drenaje): flush inmediato
    // forzado. Los items con lease de la sesión muerta se saltan hasta que el
    // TTL caduque (≤3 min) y entonces se roban.
    if (navigator.onLine) tryFlush(true);
    // El periodo inicial del poll se calculó con pendingCount=0 (el
    // refreshPending del mount aún no había resuelto) → arrancaba en IDLE (60s)
    // aunque hubiera cola. Ajustamos a BUSY en cuanto el contador real llega:
    // con el poll a ~1,5s reintentando, los leases muertos se roban al caducar
    // y la reanudación tras un F5 es SOLA y en <4 min, sin esperar el primer
    // tick de 60s.
    void refreshPending().then(() => {
      if (pendingCount > 0 && currentPeriod !== POLL_BUSY_MS) {
        startPolling(POLL_BUSY_MS);
      }
    });

    return () => {
      setSyncProgressCallback(null);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      if (connEscucha) conn.removeEventListener('change', onConnChange);
      navigator.serviceWorker?.removeEventListener('message', onSWMessage);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
      window.removeEventListener('pageshow', onVisibility);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);
}

// Notifica al store que algo cambió (cuando el cliente acaba de encolar).
// Además registramos Background Sync inmediatamente — si el usuario cierra
// la pestaña antes del primer tryFlush, el navegador procesa la cola al
// detectar conectividad. Y pedimos storage persistente para que la cola
// no se evicte aunque la PWA no esté instalada.
// Si el usuario está en manual offline, NO registramos el sync — su
// elección debe respetarse hasta que pulse "Volver a online".
export function notifyPendingChanged(): void {
  // Primer pendiente estando ya en offline manual: el plazo cuenta desde AHORA.
  // Sin esto, un modo activado hace horas caducaría en el mismo instante de
  // guardar la carga y le devolvería la red sin avisar.
  if (isUserOfflineMode() && pendingCount === 0) touchManualOfflineClock();
  refreshPending();
  if (!isUserOfflineMode()) void ensureBackgroundSyncRegistered();
  void ensurePersistentStorage();
}

// Trigger del flush — usado tras encolar y desde el botón "reintentar".
export function triggerOfflineFlush(force = false) {
  tryFlush(force);
}

// Versión "reintentar todo" — también resetea retries de items abandonados.
export function triggerForceRetry() {
  tryFlush(true, true);
}

// Hook compuesto: estado + coordinación. Para el componente padre.
export function useOfflineSync(): { pending: number } {
  useSyncCoordinator();
  const [pending, setLocal] = useState(pendingCount);
  const refreshLocal = useCallback(() => setLocal(pendingCount), []);
  useEffect(() => subscribe(refreshLocal), [refreshLocal]);
  return { pending };
}
